/* eslint-disable max-classes-per-file */
import * as debounce from 'lodash/debounce';
import { React, AppTextLabel } from '@@commons';
import type { IDropdownEditorContentProps, IDropdownEditorItem } from './index';
import { SelectableItem } from '../selectableitem';
import { AsyncButton } from '@inwink/buttons/asyncbutton';

export interface IDropdownEditorPopoverProps extends IDropdownEditorContentProps {
    onCompleted?: (res?) => void;
}

interface IDropdownEditorPopoverState {
    filter: string;
    selection: IDropdownEditorItem[];
    isSearchFieldFocus: boolean;
}

export class DropdownEditorPopover extends React.Component<IDropdownEditorPopoverProps, IDropdownEditorPopoverState> {
    filterInput = React.createRef<HTMLInputElement>();

    constructor(props: IDropdownEditorPopoverProps) {
        super(props);
        this.setFilter = debounce(this.setFilter, 300);
        this.state = {
            filter: "",
            selection: props.selection,
            isSearchFieldFocus: false
        };
    }

    componentDidMount() {
        if (this.filterInput?.current) {
            this.filterInput.current.focus();
            this.filterInput.current.oninput = (evt: Event) => {
                this.setFilter((evt.currentTarget as any).value);
                return null;
            };
        }
    }

    setFilter = (txt) => {
        this.setState({ filter: txt });
    };

    filterChanged = (arg: React.FormEvent<HTMLInputElement>) => {
        this.setFilter(arg.currentTarget.value);
    };

    onValidate = (arg: React.MouseEvent<any>) => {
        arg.preventDefault();
        arg.stopPropagation();
        this.props.onChange(this.state.selection);
        this.props.onCompleted();
    };

    selectionChange = (selection) => {
        this.setState({ selection: selection });
        if (!this.props.multiselect && selection && selection.length) {
            this.props.onChange(selection);
            this.props.onCompleted();
        }
    };

    close = (arg: React.MouseEvent<any>) => {
        arg.preventDefault();
        arg.stopPropagation();
        this.props.onCompleted();
    };

    configureItems = (arg: React.MouseEvent<any>) => {
        arg.preventDefault();
        arg.stopPropagation();
        if (this.props.configureItemsOnClick) {
            return this.props.configureItemsOnClick().then((res) => {
                if (res) {
                    this.props.onCompleted();
                }
            });
            // .then((res) => {
            //     if (res) {
            //         toastSuccess((this.props as any).i18n, "dropdowneditor.configureitems.success");
            //     }
            // });
        }
    };

    render() {
        const hasData = this.props.allItems && this.props.allItems.length;
        const showSearch = this.props.allItems && this.props.allItems.length > 10;
        let editorItems = this.props.allItems;

        if (this.state.filter) {
            const filterTokenToIgnore = this.props.prefixFilterTokenToIgnore;

            let filter = this.state.filter.toLowerCase();

            if (filterTokenToIgnore && this.state.filter?.startsWith(filterTokenToIgnore)) {
                filter = this.state.filter.substring(filterTokenToIgnore.length);
            }

            editorItems = editorItems.filter((item) => {
                return item.label.toLowerCase().indexOf(filter) >= 0;
            });
        }

        let content = null;
        const classes = ["iw-dropdowneditor-popover-items"];
        if (editorItems && editorItems.length) {
            content = editorItems.map((item) => {
                return <DropdownEditorPopoverItem
                    {...this.props}
                    key={item.key}
                    selection={this.state.selection}
                    item={item}
                    onChange={this.selectionChange}
                />;
            });
        } else {
            classes.push("empty");
            content = <AppTextLabel i18n="dropdowneditor.empty" />;
        }

        let title = null;
        if (this.props.modaleTitle) {
            if (typeof this.props.modaleTitle === "string") {
                title = <AppTextLabel className="title" i18n={this.props.modaleTitle} />;
            } else {
                title = <span className="title">{React.createElement(this.props.modaleTitle, this.props)}</span>;
            }
        }

        if (this.filterInput.current && showSearch && !this.state.isSearchFieldFocus) {
            this.filterInput.current.focus();
            this.setState({ isSearchFieldFocus: true });
        }

        return <div 
            className={`iw-dropdowneditor-popover-content ${this.props.className || ""}`}
            data-fieldkey={this.props.entityFieldFormTemplate?.key}>
            {title ? <header className="header-title">
                {title}
                {this.props.configureItemsOnClick && <AsyncButton onClick={this.configureItems}
                    className="btn-configure-items" i18ntitle="dropdowneditor.configureitems" >
                    <i className='inwink-settings-icon' />
                </AsyncButton>}
                <AsyncButton type="button" className="btnclose" onClick={this.close} i18ntitle='actions.close'>
                    <i className="inwink-dialog-cancel" />
                </AsyncButton>
            </header> : null}
            {showSearch ? <header>
                <input ref={this.filterInput} type="search" />
                <i className="inwink-search" />
            </header> : null}
            <section className={classes.join(" ")}>
                {content}
                {!hasData && this.props.configureItemsOnClick && <AsyncButton onClick={this.configureItems}
                    className="btn-configure-items blackbtn" i18ntitle="dropdowneditor.configureitems" >
                    <i className='inwink-settings-icon' />
                    <AppTextLabel i18n="dropdowneditor.configureitems" />
                </AsyncButton>}
            </section>
            {this.props.multiselect && <footer>
                <button type="button" className="important" onClick={this.onValidate}>
                    <AppTextLabel i18n="actions.validate" /></button>
            </footer>}
        </div>;
    }
}

export interface IDropdownEditorPopoverItemProps extends IDropdownEditorPopoverProps {
    item: IDropdownEditorItem;
}
export class DropdownEditorPopoverItem extends React.Component<IDropdownEditorPopoverItemProps, any> {
    itemClicked = (arg: React.MouseEvent<any>) => {
        arg.preventDefault();
        arg.stopPropagation();

        let hasExisting = false;
        let selection = (this.props.selection && this.props.selection.filter((s) => {
            if (s.key === this.props.item.key) {
                hasExisting = true;
                return false;
            }

            return true;
        })) || [];

        if (!hasExisting) {
            if (this.props.multiselect) {
                selection.push(this.props.item);
            } else {
                selection = [this.props.item];
            }
        }

        this.props.onChange(selection);
        // if (!this.props.multiselect) {
        //     this.props.onChange(selection)
        //     this.props.onCompleted();
        // }
    };

    render() {
        const isSelected = this.props.selection && this.props.selection.some((s) => s.key === this.props.item.key);
        return <SelectableItem onClick={this.itemClicked} isSelected={isSelected}
            disableSelectable={this.props.item.disableSelectable}
            className={this.props.item.className}>
            {this.props.item.label}
        </SelectableItem>;
    }
}
